// IM Properties: every unit listed on Dubai Sell, inside the app.
//
// It used to be a single button that left the app for dubaisellfast.com.
// Reads the same public /api/sell-platform/listings the Home strip uses, but
// keeps every listed unit — a unit without a photo still gets a card. An
// enquiry goes through /api/sell-platform/enquire, so it lands in Dubai Sell's
// leads table and on Slack exactly like one sent from the website.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, MapPin, BedDouble, Ruler, Search, X, Check, Eye, CalendarDays, MessageCircle } from 'lucide-react';
import {
  SellListing, fetchAllSellListings, formatAed, formatAedFull, submitEnquiry,
} from '../portals/shared/sellListings';
import { DEMO_PLATFORM_LISTINGS } from '../portals/demo/demoNotifications';
import { openWhatsApp } from '../portals/shared/contact';

interface Props {
  language: string;
  demoMode?: boolean;
  investorName?: string;
  investorEmail?: string;
  investorPhone?: string;
}

const GOLD = '#D6B160';
const GOLD_TEXT = '#B08D3C';
const RED = '#E5484D';
const WHATSAPP_GREEN = '#25D366';
const SERIF = "'Playfair Display', Georgia, serif";

// Card layout mirrors the Dubai Sell website's listing card.
const TAG_DE: Record<string, string> = {
  'PRIME LOCATION': 'TOP-LAGE', EXCLUSIVE: 'EXKLUSIV', 'PAYMENT PLAN': 'ZAHLUNGSPLAN',
  'LARGER LAYOUT': 'GRÖSSERER GRUNDRISS', 'NEW LISTING': 'NEU GELISTET', 'PRICE REDUCED': 'PREIS GESENKT',
  'HIGH ROI': 'HOHE RENDITE', 'READY TO MOVE': 'BEZUGSFERTIG', VACANT: 'LEERSTEHEND', FURNISHED: 'MÖBLIERT',
};

/** '2028-10-30' -> 'Q4 28'; a date already past -> null (the unit is ready). */
function handoverLabel(date?: string | null): string | null {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime()) || d.getTime() < Date.now()) return null;
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${String(d.getFullYear()).slice(2)}`;
}

/** Dubai Sell marks a seller motivated when they want to close within two weeks ('high' = demo data). */
const isMotivated = (l: SellListing) => l.urgency === '3days' || l.urgency === '2weeks' || l.urgency === 'high';

const shortBeds = (b: number | null, de: boolean) =>
  b == null ? null : b === 0 ? 'Studio' : de ? `${b} Zi.` : `${b}BR`;

const typeLabel = (l: SellListing, de: boolean) => {
  const t = (l.propertyType || '').toLowerCase();
  if (!t) return null;
  if (de) return ({ apartment: 'WOHNUNG', studio: 'STUDIO', villa: 'VILLA', townhouse: 'REIHENHAUS', penthouse: 'PENTHOUSE' } as Record<string, string>)[t] || t.toUpperCase();
  return t.toUpperCase();
};

const viewLine = (l: SellListing, de: boolean) => {
  const pos = l.unitPosition && l.unitPosition !== 'middle'
    ? (de ? ({ corner: 'Eckeinheit', end: 'Endeinheit' } as Record<string, string>)[l.unitPosition] || l.unitPosition : l.unitPosition)
    : null;
  return [l.viewType, pos].filter(Boolean).join(' · ') || null;
};

const askOnWhatsApp = (l: SellListing, de: boolean) => {
  const name = [l.title || l.propertyType, l.location].filter(Boolean).join(', ');
  openWhatsApp(de
    ? `Hallo, ich interessiere mich für ${name} (Dubai Sell ${l.id}).`
    : `Hi, I'm interested in ${name} (Dubai Sell ${l.id}).`);
};

const pill = (bg: string, color: string, border?: string): React.CSSProperties => ({
  fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', padding: '5px 10px', borderRadius: 999,
  background: bg, color, border: border ? `1px solid ${border}` : 'none', whiteSpace: 'nowrap',
});

const DubaiSellListingsView: React.FC<Props> = ({ language, demoMode, investorName, investorEmail, investorPhone }) => {
  const de = language === 'de';
  const [listings, setListings] = useState<SellListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [beds, setBeds] = useState<'all' | '0' | '1' | '2' | '3' | '4+'>('all');
  const [open, setOpen] = useState<SellListing | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (demoMode) {
      setListings(DEMO_PLATFORM_LISTINGS.map((l: any) => (de ? { ...l, title: l.titleDe || l.title, description: l.descriptionDe || l.description } : l)));
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true); setError(false);
    fetchAllSellListings()
      .then(r => { if (live) setListings(r.listings); })
      .catch(() => { if (live) setError(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [demoMode, de, attempt]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return listings.filter(l => {
      if (q && !`${l.title || ''} ${l.location || ''} ${l.propertyType || ''}`.toLowerCase().includes(q)) return false;
      if (beds === 'all') return true;
      const b = Number(l.bedrooms) || 0;
      return beds === '4+' ? b >= 4 : b === Number(beds);
    });
  }, [listings, query, beds]);

  const bedLabel = (b: number | null) =>
    b == null ? null : b === 0 ? 'Studio' : `${b} ${de ? 'Zimmer' : (b === 1 ? 'Bed' : 'Beds')}`;

  return (
    <div style={{ padding: '16px', paddingBottom: '110px', maxWidth: 900, margin: '0 auto' }}>
      <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#000', margin: '4px 0 4px' }}>
        {de ? 'Verfügbare Immobilien' : 'Available properties'}
      </h2>
      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 14px' }}>
        {loading
          ? (de ? 'Einheiten werden geladen…' : 'Loading units…')
          : de ? `${listings.length} Einheiten auf Dubai Sell` : `${listings.length} units listed on Dubai Sell`}
      </p>

      <div style={{ position: 'relative', marginBottom: '10px' }}>
        <Search size={16} color="#9ca3af" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={de ? 'Projekt oder Gegend suchen' : 'Search project or area'}
          style={{ width: '100%', boxSizing: 'border-box', padding: '12px 12px 12px 36px', borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 15, background: '#fff' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 16, paddingBottom: 2 }}>
        {(['all', '0', '1', '2', '3', '4+'] as const).map(b => (
          <button
            key={b}
            type="button"
            onClick={() => setBeds(b)}
            style={{
              flexShrink: 0, padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${beds === b ? '#000' : '#e5e7eb'}`,
              background: beds === b ? '#000' : '#fff', color: beds === b ? '#fff' : '#111827',
            }}
          >
            {b === 'all' ? (de ? 'Alle' : 'All') : b === '0' ? 'Studio' : `${b} ${de ? 'Zi.' : 'Bed'}`}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20, textAlign: 'center' }}>
          <p style={{ margin: '0 0 12px', color: '#374151' }}>{de ? 'Die Einheiten konnten nicht geladen werden.' : 'The units could not be loaded.'}</p>
          <button type="button" onClick={() => setAttempt(a => a + 1)} style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: '#000', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            {de ? 'Erneut versuchen' : 'Try again'}
          </button>
        </div>
      )}

      {loading && !error && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          {[0, 1, 2, 3].map(i => <div key={i} style={{ height: 270, borderRadius: 16, background: '#f3f4f6' }} />)}
        </div>
      )}

      {!loading && !error && shown.length === 0 && (
        <p style={{ color: '#6b7280', textAlign: 'center', padding: 24 }}>{de ? 'Keine passenden Einheiten.' : 'No matching units.'}</p>
      )}

      {!loading && !error && shown.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          {shown.map(l => {
            const handover = handoverLabel(l.handoverDate);
            const view = viewLine(l, de);
            const tags = (l.highlightTags || []).slice(0, 3);
            const facts = [l.location, shortBeds(l.bedrooms, de), l.sizeSqft ? `${Math.round(l.sizeSqft).toLocaleString('en-AE')} sqft` : null].filter(Boolean).join(' · ');
            return (
              <div key={l.id} style={{ border: '1px solid #EFE7D6', borderRadius: 18, background: '#FFFDF8', padding: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}>
                <button type="button" onClick={() => setOpen(l)} aria-label={l.title || undefined}
                  style={{ position: 'relative', height: 200, padding: 0, border: 'none', borderRadius: 14, overflow: 'hidden', background: '#111', cursor: 'pointer' }}>
                  {l.photoUrl
                    ? <img src={l.photoUrl} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Building2 size={40} color={GOLD} /></div>}
                  <div style={{ position: 'absolute', left: 10, right: 10, bottom: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={pill('rgba(40,36,20,0.72)', '#F3E2B3', 'rgba(214,177,96,0.6)')}>{de ? 'VERIFIZIERT' : 'VERIFIED'}</span>
                      {isMotivated(l) && <span style={pill(GOLD, '#2A2410')}>{de ? 'MOTIVIERTER VERKÄUFER' : 'MOTIVATED SELLER'}</span>}
                    </div>
                    {l.isDistress && l.discountPercent != null && (
                      <span style={{ ...pill(RED, '#fff'), fontSize: 12.5, padding: '6px 12px' }}>
                        {l.discountPercent}% {de ? 'UNTER MARKTWERT' : 'BELOW MARKET VALUE'}
                      </span>
                    )}
                  </div>
                </button>

                <div style={{ padding: '14px 6px 4px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {typeLabel(l, de) && <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', color: GOLD_TEXT }}>{typeLabel(l, de)}</div>}
                  <div style={{ fontFamily: SERIF, fontSize: 21, fontWeight: 700, color: '#1B1B1B', marginTop: 6, lineHeight: 1.25 }}>
                    {l.title || l.propertyType || (de ? 'Einheit' : 'Unit')}
                  </div>
                  {facts && <div style={{ fontSize: 14, color: '#6b6b6b', marginTop: 6 }}>{facts}</div>}
                  {view && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#6b6b6b', marginTop: 6 }}>
                      <Eye size={14} color={GOLD_TEXT} /> {view}
                    </div>
                  )}
                  {handover && (
                    <span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '4px 10px', borderRadius: 8, border: `1px solid ${GOLD}`, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', color: GOLD_TEXT }}>
                      <CalendarDays size={13} /> {de ? 'ÜBERGABE' : 'HANDOVER'}: <span style={{ color: '#1B1B1B' }}>{handover}</span>
                    </span>
                  )}
                  {tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12, paddingTop: 12, borderTop: '1px solid #EFE7D6' }}>
                      {tags.map(t => <span key={t} style={pill('#FDECEC', '#C9383D', '#F6C9CB')}>{de ? TAG_DE[t.trim().toUpperCase()] || t : t.toUpperCase()}</span>)}
                    </div>
                  )}

                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 19, fontWeight: 800, color: '#000' }}>{formatAed(l.askingPrice) || (de ? 'Preis auf Anfrage' : 'Price on request')}</div>
                    {l.isDistress && l.marketValue != null && (
                      <div style={{ fontSize: 12.5, color: '#6b6b6b', marginTop: 2 }}>{de ? 'Marktwert' : 'Market value'} {formatAed(l.marketValue)}</div>
                    )}
                  </div>

                  <div style={{ marginTop: 'auto', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button type="button" onClick={() => setOpen(l)}
                      style={{ padding: '12px', borderRadius: 12, border: '1px solid #E3D7BC', background: '#fff', color: GOLD_TEXT, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                      {de ? 'Details ansehen' : 'View Details'}
                    </button>
                    <button type="button" onClick={() => askOnWhatsApp(l, de)}
                      style={{ padding: '12px', borderRadius: 12, border: 'none', background: WHATSAPP_GREEN, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <MessageCircle size={17} /> {de ? 'Per WhatsApp chatten' : 'Chat on WhatsApp'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <ListingSheet
          listing={open}
          de={de}
          demoMode={demoMode}
          onClose={() => setOpen(null)}
          defaults={{ name: investorName || '', email: investorEmail || '', phone: investorPhone || '' }}
          bedLabel={bedLabel(open.bedrooms)}
        />
      )}
    </div>
  );
};

const ListingSheet: React.FC<{
  listing: SellListing; de: boolean; demoMode?: boolean; onClose: () => void;
  defaults: { name: string; email: string; phone: string }; bedLabel: string | null;
}> = ({ listing: l, de, demoMode, onClose, defaults, bedLabel }) => {
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState(defaults.phone);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  // The phone keyboard covers the lower half of the sheet; bring the field back up once it has opened.
  const keepInView = (e: React.FocusEvent<HTMLElement>) => {
    const el = e.currentTarget;
    setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300);
  };

  const send = async () => {
    if (!defaults.email) { setState('error'); return; }
    setState('sending');
    try {
      // The enquiry endpoint requires a name; a profile without one would be rejected.
      if (!demoMode) await submitEnquiry({ listing: l, name: defaults.name || defaults.email, email: defaults.email, phone, message });
      setState('sent');
    } catch { setState('error'); }
  };

  // Portalled to <body> above the bottom nav (z-index 9999 in index.css): inside
  // the tab it sat under the nav, which covered the message box and Send button.
  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10050, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 560, maxHeight: '92dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '20px 20px 0 0', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div style={{ position: 'relative', height: 230, background: '#111' }}>
          {l.photoUrl
            ? <img src={l.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Building2 size={48} color={GOLD} /></div>}
          <button type="button" aria-label={de ? 'Schließen' : 'Close'} onClick={onClose}
            style={{ position: 'absolute', top: 12, right: 12, width: 36, height: 36, borderRadius: 999, border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: '18px 18px 28px' }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#000' }}>{formatAedFull(l.askingPrice) || (de ? 'Preis auf Anfrage' : 'Price on request')}</div>
          {l.isDistress && l.marketValue != null && (
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
              {de ? 'Marktwert' : 'Market value'} {formatAedFull(l.marketValue)}{l.discountPercent != null ? ` · −${l.discountPercent}%` : ''}
            </div>
          )}
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 10 }}>{l.title || l.propertyType}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 14, color: '#374151', marginTop: 8 }}>
            {l.location && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={14} /> {l.location}</span>}
            {bedLabel && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><BedDouble size={14} /> {bedLabel}</span>}
            {l.sizeSqft ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Ruler size={14} /> {Math.round(l.sizeSqft).toLocaleString('en-AE')} sqft</span> : null}
            {viewLine(l, de) && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Eye size={14} /> {viewLine(l, de)}</span>}
            {handoverLabel(l.handoverDate) && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><CalendarDays size={14} /> {de ? 'Übergabe' : 'Handover'} {handoverLabel(l.handoverDate)}</span>}
          </div>
          <button type="button" onClick={() => askOnWhatsApp(l, de)}
            style={{ width: '100%', marginTop: 14, padding: 13, borderRadius: 12, border: 'none', background: WHATSAPP_GREEN, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <MessageCircle size={17} /> {de ? 'Per WhatsApp chatten' : 'Chat on WhatsApp'}
          </button>
          {l.description && <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.55, marginTop: 14, whiteSpace: 'pre-line' }}>{l.description}</p>}

          {state === 'sent' ? (
            <div style={{ marginTop: 18, padding: 16, borderRadius: 12, background: '#f0fdf4', color: '#166534', display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
              <Check size={18} /> {de ? 'Anfrage gesendet — wir melden uns bei Ihnen.' : 'Enquiry sent — we will be in touch.'}
            </div>
          ) : (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{de ? 'Interesse an dieser Einheit?' : 'Interested in this unit?'}</div>
              <input value={phone} onChange={e => setPhone(e.target.value)} onFocus={keepInView} placeholder={de ? 'Telefon (optional)' : 'Phone (optional)'}
                style={{ width: '100%', boxSizing: 'border-box', padding: 12, borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 15, marginBottom: 8 }} />
              <textarea value={message} onChange={e => setMessage(e.target.value)} onFocus={keepInView} rows={3} placeholder={de ? 'Nachricht (optional)' : 'Message (optional)'}
                style={{ width: '100%', boxSizing: 'border-box', padding: 12, borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 15, resize: 'vertical', fontFamily: 'inherit' }} />
              {state === 'error' && <p style={{ color: '#b91c1c', fontSize: 13, margin: '6px 0 0' }}>{de ? 'Senden fehlgeschlagen. Bitte erneut versuchen.' : 'Could not send. Please try again.'}</p>}
              <button type="button" onClick={send} disabled={state === 'sending'}
                style={{ width: '100%', marginTop: 10, padding: 14, borderRadius: 12, border: 'none', background: '#000', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: state === 'sending' ? 0.6 : 1 }}>
                {state === 'sending' ? (de ? 'Wird gesendet…' : 'Sending…') : (de ? 'Anfrage senden' : 'Send enquiry')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DubaiSellListingsView;
